import { useEffect } from "react";

/**
 * Set the tab title and meta description for the page currently on screen.
 *
 * The built HTML already carries the right tags for whichever URL was loaded first —
 * scripts/prerenderRoutes.mjs writes a copy of index.html per route. That covers a
 * cold load and every crawler. What it cannot cover is what happens next: this is a
 * single page app, so tapping "Hire Driver" swaps the view without fetching anything,
 * and the tab keeps the title of the page the visitor arrived on.
 *
 * That matters beyond tidiness. The title is what a browser writes into history and
 * into a bookmark, and it is what shows in the tab strip when somebody has six tabs
 * open. Leaving every route called "Sramika - Cabs & Hire Drivers in Vijayawada" makes
 * the app indistinguishable from itself.
 *
 * Deliberately small. No react-helmet, no provider, no dependency: a title and a
 * description are two DOM writes, and the og: tags are not worth touching here
 * because the scrapers that read them never run JavaScript — they only ever see the
 * prerendered file.
 *
 *   usePageMeta("Cabs in Vijayawada | Sramika", "Browse cabs in Vijayawada ...");
 *
 * Pass a falsy title to leave the document alone, which is what a page does while it
 * is still loading the thing it wants to name itself after.
 */
export function usePageMeta(title, description) {
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  useEffect(() => {
    if (!description) return;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", description);
  }, [description]);
}

export default usePageMeta;
